import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Layout, PageHeader } from "@/components/Layout";
import { SeverityBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Check, RefreshCw, BellOff } from "lucide-react";
import { toast } from "sonner";

export default function Alerts() {
  const { t } = useI18n();
  const [alerts, setAlerts] = useState([]);

  const load = async () => {
    const { data } = await api.get("/alerts?limit=200");
    setAlerts(data);
  };

  useEffect(() => {
    load();
    const tm = setInterval(load, 15000);
    return () => clearInterval(tm);
  }, []);

  const ack = async (id) => {
    await api.post(`/alerts/${id}/ack`);
    toast.success(t("alerts.acknowledgedToast"));
    load();
  };

  const fmt = (ts) => new Date(ts).toLocaleString();

  return (
    <Layout>
      <PageHeader
        title={t("alerts.title")}
        subtitle={t("alerts.subtitle")}
        actions={
          <Button variant="outline" onClick={load} data-testid="refresh-alerts">
            <RefreshCw className="mr-2 h-4 w-4" /> {t("common.refresh")}
          </Button>
        }
      />
      <div className="p-6">
        <Card className="border-slate-200 shadow-none">
          {alerts.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-slate-400">
              <BellOff className="h-8 w-8" strokeWidth={1.5} />
              <span className="text-sm">{t("alerts.noAlarms")}</span>
            </div>
          ) : (
            <Table data-testid="alerts-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("alerts.date")}</TableHead>
                  <TableHead>{t("alerts.station")}</TableHead>
                  <TableHead>{t("alerts.channel")}</TableHead>
                  <TableHead>{t("alerts.reason")}</TableHead>
                  <TableHead className="text-right">{t("alerts.value")}</TableHead>
                  <TableHead>{t("alerts.severity")}</TableHead>
                  <TableHead>{t("alerts.state")}</TableHead>
                  <TableHead className="text-right">{t("alerts.action")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alerts.map((a) => (
                  <TableRow key={a.id} data-testid={`alert-row-${a.id}`} className={a.acknowledged ? "opacity-50" : ""}>
                    <TableCell className="font-mono text-xs text-slate-500">{fmt(a.timestamp)}</TableCell>
                    <TableCell>
                      <div className="text-sm font-medium text-slate-800">{a.station_name}</div>
                      <div className="font-mono text-xs text-slate-400">{a.code}</div>
                    </TableCell>
                    <TableCell className="text-sm">{a.channel_name} ({a.channel})</TableCell>
                    <TableCell className="text-sm text-slate-700">{a.reason}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{a.value}</TableCell>
                    <TableCell><SeverityBadge severity={a.severity} /></TableCell>
                    <TableCell>
                      <span className={`text-xs font-medium ${a.acknowledged ? "text-slate-400" : "text-blue-600"}`}>
                        {a.acknowledged ? t("alerts.acknowledged") : t("alerts.active")}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {!a.acknowledged && (
                        <Button variant="ghost" size="sm" data-testid={`ack-${a.id}`}
                          onClick={() => ack(a.id)} className="text-emerald-600 hover:text-emerald-700">
                          <Check className="mr-1 h-4 w-4" /> {t("alerts.acknowledge")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
    </Layout>
  );
}
